import {Select, Spin} from 'antd';
import type {SelectProps} from 'antd/es/select';
import debounce from 'lodash/debounce';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from "styled-components";
import ChevronDownIcon from "../../../media/icons/chevron-down.svg?react";
import {store} from "../../../store/store";
import {getInitialCurrencyForSelect} from "../../../utils/helpers/selectDataFetchers/fetchCurrencies";
import {fetchInitialAirportForSelect} from "../../../utils/helpers/selectDataFetchers/fetchAirports";

type CustomSelectProps = SelectProps & { $brightPlaceholder: boolean };

const CustomSelect = styled(Select)<CustomSelectProps>`
  .ant-select-selection-placeholder {
    color: ${props => props.$brightPlaceholder ? "#000000" : "#bfbfbf"};
  }
`;

export interface DebounceSelectOptionProps {
  key?: number;
  label: string;
  value: string | number;
}

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions?: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  defaultOptions?: DebounceSelectOptionProps[];
  defaultOptionsFetchCallback?: (search?: string) => Promise<DebounceSelectOptionProps[] | undefined>
  brightPlaceholder?: boolean;
  dropdownMinWidth?: number;
  enableIndeterminate?: boolean;
}

export function DebounceSelect<ValueType extends DebounceSelectOptionProps = any,
  >({
      fetchOptions,
      debounceTimeout = 1000,
      defaultOptions = [],
      defaultOptionsFetchCallback = undefined,
      brightPlaceholder = false,
      suffixIcon,
  dropdownMinWidth,
  dropdownStyle,
  dropdownMatchSelectWidth,
  enableIndeterminate: _enableIndeterminate,
  ...props
    }: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>(
    defaultOptions as ValueType[]
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const fetchRef = useRef(0);
  const initialLoadRef = useRef(false);
  const initialOptionsLoadedRef = useRef(false);
  const defaultCurrencySetRef = useRef(false);

  // Вспомогательная функция для извлечения ID из value
  const extractIdFromValue = useCallback((val: any): string | number | undefined => {
    if (typeof val === 'object' && val !== null) {
      if ('value' in val) return val.value;
      if ('key' in val) return val.key;
    }
    return val;
  }, []);

  // Вспомогательная функция для определения типа селекта
  const isSelectType = useCallback((type: 'airport' | 'currency' | 'timezone'): boolean => {
    if (!fetchOptions) return false;
    const fetchOptionsStr = fetchOptions.toString();
    const functionName = fetchOptions.name || '';
    
    switch (type) {
      case 'airport':
        return fetchOptionsStr.includes('fetchAirports') || 
               fetchOptionsStr.includes('fetchAllAirports') ||
               functionName === 'fetchAirports' ||
               functionName === 'fetchAllAirports';
      case 'currency':
        return fetchOptionsStr.includes('fetchCurrencies') ||
               functionName === 'fetchCurrencies';
      case 'timezone':
        return fetchOptionsStr.includes('fetchTimezones') ||
               functionName === 'fetchTimezones';
      default:
        return false;
    }
  }, [fetchOptions]);

  // Получаем опции из стора для выбранных значений (для аэропортов и валют)
  // Для аэропортов также загружаем недостающие из API
  useEffect(() => {
    if (props.value) {
      const state = store.getState();
      
      // Проверяем аэропорты
      const airports = state.airport?.airports;
      if (airports && airports.length > 0 && (isSelectType('airport') || !fetchOptions)) {
        const valueArray = Array.isArray(props.value) ? props.value : [props.value];
        
        // Extract IDs from values
        const valueIds = valueArray
          .map(val => extractIdFromValue(val))
          .filter((id): id is number => id !== undefined && typeof id === 'number');
        
        // Find airports in store
        const selectedAirports = valueIds
          .map(id => airports.find(a => a.value === id || a.key === id))
          .filter(Boolean) as ValueType[];
        
        // Find missing airport IDs
        const foundIds = new Set(selectedAirports.map(a => a.value as number));
        const missingIds = valueIds.filter(id => !foundIds.has(id));
        
        // Add found airports to options
        if (selectedAirports.length > 0) {
          setOptions(prevOptions => {
            const existingIds = new Set(prevOptions.map(opt => opt.value));
            const newOptions = selectedAirports.filter(opt => !existingIds.has(opt.value));
            return newOptions.length > 0 ? [...prevOptions, ...newOptions] : prevOptions;
          });
        }
        
        // Fetch missing airports from API
        if (missingIds.length > 0 && isSelectType('airport')) {
          Promise.all(
            missingIds.map(id => fetchInitialAirportForSelect(id))
          ).then(results => {
            const fetchedAirports = results
              .filter((result): result is DebounceSelectOptionProps[] => result !== undefined && result.length > 0)
              .flat() as ValueType[];
            
            if (fetchedAirports.length > 0) {
              setOptions(prevOptions => {
                const existingIds = new Set(prevOptions.map(opt => opt.value));
                const newOptions = fetchedAirports.filter(opt => !existingIds.has(opt.value));
                return newOptions.length > 0 ? [...prevOptions, ...newOptions] : prevOptions;
              });
            }
          }).catch(error => {
            console.error('Error fetching missing airports:', error);
          });
        }
      }
      
      // Проверяем валюты
      const currencies = state.currency?.currencies;
      if (currencies && currencies.length > 0 && (isSelectType('currency') || !fetchOptions)) {
        const valueArray = Array.isArray(props.value) ? props.value : [props.value];
        const selectedCurrencies = valueArray
          .map(val => {
            const id = extractIdFromValue(val);
            return id !== undefined ? currencies.find(c => c.value === id || c.key === id) : null;
          })
          .filter(Boolean) as ValueType[];
        
        if (selectedCurrencies.length > 0) {
          setOptions(prevOptions => {
            const existingIds = new Set(prevOptions.map(opt => opt.value));
            const newOptions = selectedCurrencies.filter(opt => !existingIds.has(opt.value));
            return newOptions.length > 0 ? [...prevOptions, ...newOptions] : prevOptions;
          });
        }
      }
    }
  }, [props.value, fetchOptions, extractIdFromValue, isSelectType]);

  // Автоматически подставляем валюту из профиля компании, если value не задан и это селект валют
  useEffect(() => {
    // Проверяем, что это селект валют
    if (!isSelectType('currency')) {
      defaultCurrencySetRef.current = false;
      return;
    }
    
    // Проверяем, что value не задан (undefined или null)
    const hasValue = props.value !== undefined && props.value !== null;
    
    // Если значение задано, сбрасываем флаг (пользователь установил значение вручную)
    // Если значение очищено, тоже сбрасываем флаг, чтобы валюта могла установиться снова
    if (hasValue) {
      defaultCurrencySetRef.current = false;
      return;
    }
    
    // Если валюта уже была установлена автоматически и value все еще пустой, не устанавливаем повторно
    // (чтобы избежать бесконечных циклов при изменении value)
    if (defaultCurrencySetRef.current) return;
    
    // Получаем валюту компании из store
    const state = store.getState();
    const company = state.company?.oneItemObject?.item;
    if (!company?.currency?.id) return;
    
    // Проверяем, что onChange существует
    if (!props.onChange) return;
    
    // Устанавливаем валюту компании как значение по умолчанию
    const defaultCurrency = getInitialCurrencyForSelect(company.currency);
    defaultCurrencySetRef.current = true;
    
    // Вызываем onChange для установки значения по умолчанию
    // Используем requestAnimationFrame, чтобы избежать вызова во время рендера
    requestAnimationFrame(() => {
      // Проверяем еще раз, что value все еще пустой (на случай, если он изменился)
      const currentValue = props.value;
      if (props.onChange && (currentValue === undefined || currentValue === null)) {
        // Передаем значение в правильном формате для Select
        // defaultCurrency соответствует DebounceSelectOptionProps, который расширяет ValueType
        props.onChange(defaultCurrency.value as unknown as ValueType, defaultCurrency as unknown as ValueType);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- props.value, props.onChange sufficient
  }, [isSelectType, props.value, props.onChange]);

  const fetchInitialOptions = useCallback(async () => {
    if (defaultOptionsFetchCallback)
      return await defaultOptionsFetchCallback();
  }, [defaultOptionsFetchCallback]);

  useEffect(() => {
    fetchInitialOptions().then(res => {
      if (res) setOptions(res as ValueType[])
    })
  }, [fetchInitialOptions])

  // Предзагружаем опции при монтировании только если нет fetchOptions
  // Если есть fetchOptions, загрузка будет происходить при onFocus
  useEffect(() => {
    if (initialLoadRef.current) return;
    
    if (defaultOptionsFetchCallback && !fetchOptions) {
      // Если есть только defaultOptionsFetchCallback без fetchOptions, загружаем сразу
      initialLoadRef.current = true;
      fetchInitialOptions().then(res => {
        if (res && res.length > 0) {
          setOptions(res as ValueType[])
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string, clearOptions: boolean = true) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      if (clearOptions) {
        setOptions([]);
      }
      setFetching(true);

      if (fetchOptions) {
        // Always fetch, even with empty string, to get fresh results
        fetchOptions(value || "")
          .then(newOptions => {
            if (fetchId !== fetchRef.current) {
              // for fetch callback order
              return;
            }
            setOptions(newOptions);
            setFetching(false);
          })
          .catch(error => {
            if (fetchId !== fetchRef.current) {
              return;
            }
            console.error('Error fetching options:', error);
            setFetching(false);
            setOptions([]);
          });
      }
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  // Предзагружаем первые опции из store при монтировании (для аэропортов)
  useEffect(() => {
    if (initialOptionsLoadedRef.current || !fetchOptions) return;
    
    const isAirport = isSelectType('airport');
    if (isAirport) {
      initialOptionsLoadedRef.current = true;
      // Загружаем первые опции синхронно из store без debounce
      fetchOptions("").then(initialOptions => {
        if (initialOptions && initialOptions.length > 0) {
          setOptions(initialOptions);
        }
      }).catch(() => {
        // Игнорируем ошибки при предзагрузке
      });
    }
  }, [fetchOptions, isSelectType]);

  const loadInitialOptions = useCallback(async () => {
    // Загружаем опции при открытии через fetchOptions
    // Если поле поиска пустое, загружаем все опции
    if (fetchOptions) {
      // Если поле поиска пустое, загружаем все опции
      if (!searchValue || searchValue.trim() === '') {
        try {
          setFetching(true);
          // Вызываем fetchOptions напрямую без debounce для пустого запроса
          // Это загружает все доступные опции
          const initialOptions = await fetchOptions("");
          setOptions(initialOptions);
          setFetching(false);
          initialOptionsLoadedRef.current = true;
        } catch (error) {
          console.error('Error loading initial options:', error);
          setFetching(false);
          setOptions([]);
        }
      } else {
        // Если есть поисковый запрос, используем debounceFetcher для поиска
        if (debounceFetcher) {
          debounceFetcher(searchValue);
        }
      }
    }
  }, [fetchOptions, searchValue, debounceFetcher]);

  const onDropdownVisibleChange = (open: boolean) => {
    // При открытии dropdown загружаем опции
    if (open) {
      // Если поле поиска пустое, загружаем все опции
      // Если есть текст в поле поиска, используем его для фильтрации
      loadInitialOptions();
    } else {
      // Clear search value when closing
      setSearchValue('');
    }
    // Вызываем пользовательский обработчик, если он есть
    if (props.onDropdownVisibleChange) {
      props.onDropdownVisibleChange(open);
    }
  }

  const defaultSuffixIcon = <ChevronDownIcon style={{width: '12px', height: '12px', pointerEvents: 'none'}} />;

  // Автоматически определяем, является ли это селектом аэропортов
  const isAirportSelect = useMemo(() => isSelectType('airport'), [isSelectType]);

  // Автоматически определяем, является ли это селектом таймзон
  const isTimezoneSelect = useMemo(() => isSelectType('timezone'), [isSelectType]);

  // Автоматически устанавливаем минимальную ширину для аэропортов и таймзон, если не указана явно
  const effectiveDropdownMinWidth = useMemo(() => {
    if (dropdownMinWidth) return dropdownMinWidth;
    if (isAirportSelect) return 250; // Ширина по умолчанию для аэропортов в пикселях
    if (isTimezoneSelect) return 200; // Ширина по умолчанию для таймзон в пикселях
    return undefined;
  }, [dropdownMinWidth, isAirportSelect, isTimezoneSelect]);

  const mergedDropdownStyle = useMemo(() => ({
    borderRadius: 5,
    ...(effectiveDropdownMinWidth ? {minWidth: effectiveDropdownMinWidth} : {}),
    ...dropdownStyle
  }), [effectiveDropdownMinWidth, dropdownStyle]);

  // Деструктурируем getPopupContainer и placement из props, чтобы можно было переопределить
  const {getPopupContainer: propsGetPopupContainer, placement: propsPlacement, ...restProps} = props;

  // Normalize value prop: convert IDs to full option objects when available in options
  const normalizedValue = useMemo(() => {
    if (!props.value) return props.value;
    
    // For airport selects, normalize IDs to option objects
    if (isSelectType('airport') && options.length > 0) {
      const isMultiple = Array.isArray(props.value);
      const valueArray: any[] = isMultiple ? (props.value as any[]) : [props.value];
      
      const normalized = valueArray.map((val: any) => {
        // If already an object with label, return as is
        if (typeof val === 'object' && val !== null && 'label' in val && val.label) {
          return val;
        }
        
        // Extract ID
        const id = extractIdFromValue(val);
        if (id === undefined) return val;
        
        // Find matching option in options array
        const option = options.find(opt => opt.value === id || opt.key === id);
        if (option) {
          return option;
        }
        
        // If not found in options, return original value (will be fetched by useEffect)
        return val;
      });
      
      // Return in same format as input (array or single value)
      return isMultiple ? normalized : (normalized[0] || props.value);
    }
    
    return props.value;
  }, [props.value, options, isSelectType, extractIdFromValue]);

  // Custom onSearch handler that ensures empty string triggers a fetch
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    if (fetchOptions && debounceFetcher) {
      // Always call debounceFetcher, even with empty string
      debounceFetcher(value || "");
    }
  }, [fetchOptions, debounceFetcher]);

  // Handle deselection - re-fetch options based on current search
  // This ensures dropdown shows correct results after removing items
  const handleDeselect = useCallback((value: any, option?: any) => {
    // Call user's onDeselect if provided (with both parameters)
    if (props.onDeselect) {
      props.onDeselect(value, option);
    }
    
    // Use a small delay to ensure search input value is updated
    // Then re-fetch options based on current search value from the DOM
    setTimeout(() => {
      // Try to get current search value from the DOM
      const searchInput = document.querySelector('.ant-select-selection-search-input') as HTMLInputElement;
      const currentSearchValue = searchInput?.value || searchValue || '';
      
      if (fetchOptions && debounceFetcher) {
        setSearchValue(currentSearchValue);
        debounceFetcher(currentSearchValue);
      }
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- props.onDeselect and rest sufficient
  }, [props.onDeselect, fetchOptions, debounceFetcher, searchValue]);

  // Рендер dropdown
  const renderDropdown = useCallback((menu: React.ReactElement) => {
    // Если есть пользовательский dropdownRender, применяем его
    return props.dropdownRender ? props.dropdownRender(menu) : menu;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- props.dropdownRender sufficient
  }, [props.dropdownRender]);

  // Определяем контент для случая загрузки
  const notFoundContent = useMemo(() => {
    if (fetching) {
      return <div style={{display: 'flex', justifyContent: 'center', padding: '16px'}}><Spin size="small"/></div>;
    }
    
    // Не показываем текст "No data", просто возвращаем null
    return null;
  }, [fetching]);

  return (
    // @ts-expect-error - Type issues with styled component and Select props
    <CustomSelect
      filterOption={false}
      onSearch={handleSearch}
      onDeselect={handleDeselect}
      showSearch={true}
      onFocus={loadInitialOptions}
      onDropdownVisibleChange={onDropdownVisibleChange}
      loading={fetching}
      notFoundContent={notFoundContent}
      options={options}
      value={normalizedValue}
      allowClear={true}
      dropdownStyle={mergedDropdownStyle}
      dropdownMatchSelectWidth={effectiveDropdownMinWidth ? false : dropdownMatchSelectWidth}
      placement={propsPlacement || "bottomLeft"}
      getPopupContainer={propsGetPopupContainer || ((trigger: HTMLElement) => document.body)}
      dropdownRender={renderDropdown}
      $brightPlaceholder={brightPlaceholder}
      suffixIcon={suffixIcon !== undefined ? suffixIcon : defaultSuffixIcon}
      {...restProps}
    />
  );
}
